import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { learningHooksApi, importApi } from '../../services/api';
import { HookDashboard } from '../../components/hook/HookDashboard';
import { LearningHookCreator } from '../../components/learning_hook/LearningHookCreator';
import { StudentImport } from '../../components/student/StudentImport';
import { StudentManagement } from '../../components/student/StudentManagement';
import { EvidenceReviewPanel } from '../../components/teacher/EvidenceReviewPanel';
import { TaskManagerPanel } from '../../components/teacher/TaskManagerPanel';
import { LiveInstructionPanel } from '../../components/teacher/LiveInstructionPanel';

interface LearningHook {
  id: number;
  title: string;
  description: string;
  grade_levels: string;
  project_type: string;
  max_students: number;
  enrolled_count: number;
  is_active: boolean;
  created_at: string;
  teacher_name: string;
}

export const TeacherDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [learningHooks, setLearningHooks] = useState<LearningHook[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'hooks' | 'tasks' | 'live' | 'evidence' | 'import' | 'students'>('overview');
  const [showHookCreator, setShowHookCreator] = useState(false);

  const fetchData = async () => {
    try {
      const hooksResponse = await learningHooksApi.getAll();
      // Handle the response structure: {"learning_hooks": [...]}
      const hooksData = hooksResponse.data.learning_hooks || hooksResponse.data || [];
      setLearningHooks(Array.isArray(hooksData) ? hooksData : []);
      setError('');
    } catch (error: any) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleHookCreated = () => {
    setShowHookCreator(false);
    fetchData();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Teacher Dashboard</h1>
              <p className="text-gray-600">Welcome back, {user?.first_name || user?.email}</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm text-gray-500">Total Hooks</p>
                <p className="text-2xl font-bold text-blue-600">{learningHooks.length}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {['overview', 'hooks', 'tasks', 'live', 'evidence', 'import', 'students'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab === 'hooks' ? 'Learning Hooks' : tab}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div>
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Overview</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <span className="text-blue-600 font-bold text-xs">🎯</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Learning Hooks</p>
                      <p className="text-2xl font-bold text-gray-900">{learningHooks.length}</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">Active learning projects</p>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <span className="text-green-600 font-bold text-xs">✓</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Active</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {learningHooks.filter(h => h.is_active).length}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">Currently running</p>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <span className="text-purple-600 font-bold text-xs">📚</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Grade Levels</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {Array.from(new Set(learningHooks.map(h => h.grade_levels))).length}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">Different grades</p>
                </div>
              </div>
            </div>

            {/* Recent Learning Hooks */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Learning Hooks</h3>
              {learningHooks.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-6 text-center">
                  <p className="text-gray-500 mb-4">No learning hooks yet</p>
                  <button
                    onClick={() => setActiveTab('hooks')}
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                  >
                    Create Your First Learning Hook
                  </button>
                </div>
              ) : (
                <div className="grid gap-4">
                  {learningHooks.slice(0, 3).map((hook) => (
                    <div key={hook.id} className="bg-white rounded-lg shadow p-4">
                      <h4 className="font-medium text-lg">{hook.title}</h4>
                      <p className="text-gray-600 text-sm mt-1">{hook.description}</p>
                      <div className="flex justify-between items-center mt-3">
                        <span className="text-sm text-gray-500">{hook.grade_levels}</span>
                        <span className={`px-2 py-1 rounded text-xs ${
                          hook.is_active 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {hook.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Learning Hooks Tab */}
        {activeTab === 'hooks' && (
          <HookDashboard isTeacher={true} />
        )}

        {/* Tasks Tab */}
        {activeTab === 'tasks' && (
          <TaskManagerPanel />
        )}

        {/* Live Tab */}
        {activeTab === 'live' && (
          <LiveInstructionPanel />
        )}

        {/* Evidence Tab */}
        {activeTab === 'evidence' && (
          <EvidenceReviewPanel />
        )}

        {/* Import Tab */}
        {activeTab === 'import' && (
          <StudentImport />
        )}

        {/* Students Tab */}
        {activeTab === 'students' && (
          <StudentManagement />
        )}
      </div>

      {/* Learning Hook Creator Modal */}
      {showHookCreator && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <LearningHookCreator 
              onSuccess={handleHookCreated} 
              onCancel={() => setShowHookCreator(false)} 
            />
          </div>
        </div>
      )}
    </div>
  );
};
